// src/screens/admin/biddingManagement/BiddingManagement.jsx

import axios from "axios";
import AppContext from "../../../context/AppContext";
import { useState, useEffect, useContext } from "react";
import {
  Clock,
  CircleDot,
  Users,
  CheckCircle,
  Search,
  Check,
  X,
  Eye,
} from "lucide-react";
import AdminBiddingDetails from "../../../subscreen/admin/adminBiddingDetails/AdminBiddingDetails";
import RejectBiddingModal from "../../../components/admin/rejectBidddingModal/RejectBiddingModal";
import "../../../assets/css/admin/adminBiddingManagement/BiddingManagement.css";

const BiddingManagement = () => {
  const { baseUrl, baseUrlForFile, authToken } = useContext(AppContext);
  const [biddings, setBiddings] = useState([]);
  const [selectedBidding, setSelectedBidding] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const viewAllBiddings = () => {

    axios.get(
      `${baseUrl}/admin/bidding/viewallbiddings`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((res) => {
        console.log(res.data);
        setBiddings(res.data.biddings || []);
      })
      .catch((err) => {
        console.log(err.response?.data || err.message);
      });

  };

  useEffect(() => {
    viewAllBiddings();
  }, []);

  // ============================================
  // STATISTICS
  // ============================================
  const getStatistics = () => {
    const pending = biddings.filter((b) => b.status === "pending").length;
    const active = biddings.filter(
      (b) => b.status === "active",
    ).length;
    const ended = biddings.filter((b) => b.status === "ended").length;
    const rejected = biddings.filter(
      (b) => b.status === "rejected",
    ).length;
    const totalBids = biddings.reduce(
  (sum, b) => sum + (b.adplacebid?.length || 0),
  0
);
    return { pending, active, ended, rejected, totalBids };
  };

  const stats = getStatistics();

  // ============================================
  // ACTIONS - Replace with API calls
  // ============================================

  // api.approveBidding(biddingId)
  const approveBidding = (biddingId) => {

  const formData = new FormData();
  formData.append("adbidding_id", biddingId);

  axios.post(
    `${baseUrl}/admin/bidding/approvebidding`,
    formData ,
    { 
      headers: {
        Authorization: `Bearer ${authToken}`,
      }
    }

  )
  .then((res) => {

    console.log(res.data);

    if (res.data.success) {
      viewAllBiddings();
    }

  })
  .catch((err) => {
    console.log(err.response?.data || err.message);
  });

};

  //api.rejectBidding(biddingId, reason)
  const rejectBidding = (biddingId, reason) => {

  const formData = new FormData();

  formData.append("adbidding_id", biddingId);
  formData.append("reason", reason);

  axios.post(
    `${baseUrl}/admin/bidding/rejectbidding`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${authToken}`,
      }
    }
  )
  .then((res) => {

    console.log(res.data);

    if (res.data.success) {
      viewAllBiddings();
    }

  })
  .catch((err) => {
    console.log(err.response?.data || err.message);
  });

};

  // await api.endBiddingEarly(biddingId, reason)
  const endBiddingEarly = (biddingId, reason) => {
    setBiddings(
      biddings.map((b) =>
        b.id === biddingId
          ? {
            ...b,
            status: "ended",
            endDate: new Date().toISOString(),
            adminEndReason: reason,
          }
          : b,
      ),
    );
  };

  // ============================================
  // FILTERING
  // ============================================
  const getFilteredBiddings = () => {
    let filtered = biddings;

    if (filterStatus === "pending")
      filtered = filtered.filter((b) => b.status === "pending");
    else if (filterStatus === "active")
      filtered = filtered.filter(
        (b) => b.status === "active",
      );
    else if (filterStatus === "ended")
      filtered = filtered.filter((b) => b.status === "ended");
    else if (filterStatus === "rejected")
      filtered = filtered.filter((b) => b.status === "rejected");

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
     filtered = filtered.filter(
  (b) =>
    b.productName?.toLowerCase().includes(q) ||
    b.adcustomer?.fullName?.toLowerCase().includes(q) ||
    String(b.id).includes(q)
);
    }

    return filtered;
  };

  const filteredBiddings = getFilteredBiddings();

  // ============================================
  // HELPERS
  // ============================================
  const formatPrice = (price) =>
    new Intl.NumberFormat("en-PK", {
      style: "currency",
      currency: "PKR",
      minimumFractionDigits: 0,
    }).format(price);

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString("en-PK", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

  const getStatusBadge = (bidding) => {
    if (bidding.status === "pending")
      return <span className="bm-status-badge bm-pending">Pending</span>;
    if (bidding.status === "rejected")
      return <span className="bm-status-badge bm-rejected">Rejected</span>;
    if (bidding.status === "ended")
      return <span className="bm-status-badge bm-ended">Ended</span>;
    if (bidding.status === "active")
      return <span className="bm-status-badge bm-active">Active</span>;
    return <span className="bm-status-badge bm-upcoming">Upcoming</span>;
  };

  const handleQuickApprove = (e, bidding) => {
    e.stopPropagation();
    if (window.confirm(`Approve "${bidding.productName}"?`))
      approveBidding(bidding.id);
  };

  const handleQuickReject = (e, bidding) => {
    e.stopPropagation();
    setShowRejectModal(bidding);
  };

  const handleRejectSubmit = (reason) => {
    if (showRejectModal) {
      rejectBidding(showRejectModal.id, reason);
      setShowRejectModal(null);
    }
  };

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="bm-container">
      {/* Header */}
      <div className="bm-header">
        <h1 className="bm-title">Bidding Management</h1>
        <p className="bm-subtitle">Manage and monitor all customer biddings</p>
      </div>

      {/* Stats Cards */}
      <div className="bm-stats-grid">
        <div className="bm-stat-card">
          <div className="bm-stat-icon bm-icon-pending">
            <Clock size={22} />
          </div>
          <div className="bm-stat-content">
            <div className="bm-stat-value">{stats.pending}</div>
            <div className="bm-stat-label">Pending Approval</div>
          </div>
        </div>

        <div className="bm-stat-card">
          <div className="bm-stat-icon bm-icon-active">
            <CircleDot size={22} />
          </div>
          <div className="bm-stat-content">
            <div className="bm-stat-value">{stats.active}</div>
            <div className="bm-stat-label">Active Biddings</div>
          </div>
        </div>

        <div className="bm-stat-card">
          <div className="bm-stat-icon bm-icon-bids">
            <Users size={22} />
          </div>
          <div className="bm-stat-content">
            <div className="bm-stat-value">{stats.totalBids}</div>
            <div className="bm-stat-label">Total Bids</div>
          </div>
        </div>

        <div className="bm-stat-card">
          <div className="bm-stat-icon bm-icon-ended">
            <CheckCircle size={22} />
          </div>
          <div className="bm-stat-content">
            <div className="bm-stat-value">{stats.ended}</div>
            <div className="bm-stat-label">Ended Biddings</div>
          </div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="bm-controls">
        <div className="bm-filters">
          {[
            { key: "all", label: "All", count: biddings.length },
            { key: "pending", label: "Pending", count: stats.pending },
            { key: "active", label: "Active", count: stats.active },
            { key: "ended", label: "Ended", count: stats.ended },
            { key: "rejected", label: "Rejected", count: stats.rejected },
          ].map(({ key, label, count }) => (
            <button
              key={key}
              className={`bm-filter-btn ${filterStatus === key ? "active" : ""}`}
              onClick={() => setFilterStatus(key)}
            >
              {label}
              <span className="bm-filter-count">{count}</span>
            </button>
          ))}
        </div>

        <div className="bm-search">
          <Search size={16} className="bm-search-icon" />
          <input
            type="text"
            placeholder="Search by product, seller, or ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="bm-search-input"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bm-table-container">
        {filteredBiddings.length === 0 ? (
          <div className="bm-empty">
            <div className="bm-empty-icon">📭</div>
            <h3>No biddings found</h3>
            <p>Try adjusting your filters or search query</p>
          </div>
        ) : (
          <table className="bm-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Product</th>
                <th>Seller</th>
                <th>Current Bid</th>
                <th>Bids</th>
                <th>Status</th>
                <th>Submitted</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBiddings.map((bidding) => (
                <tr
                  key={bidding.id}
                  onClick={() => setSelectedBidding(bidding)}
                >
                  <td className="bm-id">{bidding.id}</td>
                  <td>
                    <div className="bm-product">
                      <span className="bm-product-emoji">
                        <img
                          src={
                            bidding.adcustomerbidsimage?.length > 0
                              ? `${baseUrlForFile}${bidding.adcustomerbidsimage[0].bidImages}`
                              : ""
                          }
                          alt={bidding.productName}
                          className="bm-product-img"
                        />
                      </span>
                      <div>
                        <div className="bm-product-name">
                          {bidding.productName}
                        </div>
                        <div className="bm-product-category">
                          {bidding.category}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="bm-seller-name">
                      {bidding.adcustomer?.fullName || "N/A"}
                    </div>
                    <div className="bm-seller-location">{bidding.location}</div>
                  </td>
                  <td className="bm-price">
                    {formatPrice(bidding.currentBid || bidding.startingPrice)}
                  </td>
                  <td className="bm-bids">{bidding.adplacebid?.length || 0}</td>
                  <td>{getStatusBadge(bidding)}</td>
                  <td className="bm-date">{formatDate(bidding.created_at)}</td>
                  <td>
                    <div className="bm-actions">
                      {bidding.status === "pending" && (
                        <>
                          <button
                            className="bm-action-btn bm-approve"
                            onClick={(e) => handleQuickApprove(e, bidding)}
                            title="Approve"
                          >
                            <Check size={15} />
                          </button>
                          <button
                            className="bm-action-btn bm-reject"
                            onClick={(e) => handleQuickReject(e, bidding)}
                            title="Reject"
                          >
                            <X size={15} />
                          </button>
                        </>
                      )}
                      <button
                        className="bm-action-btn bm-view"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedBidding(bidding);
                        }}
                        title="View Details"
                      >
                        <Eye size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modals */}
      {selectedBidding && (
        <AdminBiddingDetails
          bidding={selectedBidding}
          isOpen={!!selectedBidding}
          onClose={() => setSelectedBidding(null)}
          onApprove={approveBidding}
          onReject={rejectBidding}
          onEndEarly={endBiddingEarly}
        />
      )}

      {showRejectModal && (
        <RejectBiddingModal
          isOpen={!!showRejectModal}
          onClose={() => setShowRejectModal(null)}
          bidding={showRejectModal}
          onReject={handleRejectSubmit}
        />
      )}
    </div>
  );
};

export default BiddingManagement;
